import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAPI } from "../api/useAPI";

export type LoginContextState = {
  username: string | undefined;
  password: string | undefined;
  isLoggedIn: boolean | undefined;
  setLogin: (args: { username: string; password: string }) => void;
  logout: () => void;
};

export const initialCardsContext: LoginContextState = {
  username: undefined,
  password: undefined,
  isLoggedIn: undefined,
  setLogin: () => {},
  logout: () => {},
};
const LoginContext = createContext(initialCardsContext);

export const useLoginContext = () => useContext(LoginContext);

export default function LoginContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [isLoggedIn, setLoggedIn] = useState<boolean | undefined>(undefined);
  const { checkAuthentication } = useAPI();

  // On mount, check localStorage for credentials
  useEffect(() => {
    const saved = localStorage.getItem("loginCredentials");
    if (saved) {
      try {
        const { username, password } = JSON.parse(saved);
        if (username && password) {
          checkAuthentication(username, password).then((valid) => {
            if (valid) {
              setUsername(username);
              setPassword(password);
              setLoggedIn(true);
            } else {
              if (valid === false) {
                localStorage.removeItem("loginCredentials");
              }
              setLoggedIn(false);
            }
          });
        }
      } catch {}
    } else {
      setLoggedIn(false);
    }
  }, [checkAuthentication]);

  const logout = useCallback(() => {
    setUsername(undefined);
    setPassword(undefined);
    setLoggedIn(false);
    localStorage.removeItem("loginCredentials");
  }, []);

  const setLogin = useCallback(
    ({ username, password }: { username: string; password: string }) => {
      setUsername(username);
      setPassword(password);
      setLoggedIn(true);
      localStorage.setItem(
        "loginCredentials",
        JSON.stringify({ username, password })
      );
    },
    []
  );

  return (
    <LoginContext.Provider
      value={{
        username,
        password,
        isLoggedIn,
        setLogin,
        logout,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
}
